/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Input, OnChanges, signal} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';
import {deferSignalStateWrite} from '../../util/template-state';
import {ADMIN_LIST_IMPORTS} from '../admin-list-imports';

@Component({
  standalone: true,
  imports: ADMIN_LIST_IMPORTS,
  selector: 'undo-delete',
  template: `
    <div *ngIf="undoItemCount" class="alert alert-success">
      <p>
        {{'message.undo' + currentAction + '.text' | translate: {size: undoItemCount} }}&nbsp;
        <a (click)="undoList.undoBulkAction(param)">{{'message.undo' + currentAction + '.linkText' | translate}}&nbsp;</a>
      </p>
    </div>
    <div *ngIf="undoBulkSelectionCount" class="alert alert-success">
      <p>
        Bulk edit on {{undoBulkSelectionCount}} selections was successful.
        <a (click)="undoList.undoMiscBulkAction(param)">Undo the bulk edit?</a>
      </p>
    </div>
  `
})
export class UndoDeleteComponent implements OnChanges, DoCheck {
  @Input() undoList: any;
  @Input() param: string;

  private readonly undoItemCountState = signal(0);
  private readonly undoBulkSelectionCountState = signal(0);
  private readonly currentActionState = signal<any>(undefined);

  get undoItemCount(): number { return this.undoItemCountState(); }
  set undoItemCount(value: number) { this.undoItemCountState.set(value); }
  get undoBulkSelectionCount(): number { return this.undoBulkSelectionCountState(); }
  set undoBulkSelectionCount(value: number) { this.undoBulkSelectionCountState.set(value); }
  get currentAction(): any { return this.currentActionState(); }
  set currentAction(value: any) { this.currentActionState.set(value); }

  private initialized = false;
  private syncScheduled = false;

  constructor(public translate: TranslateService) {
  }

  ngOnChanges(): void {
    this.scheduleSnapshot();
  }

  ngDoCheck(): void {
    this.scheduleSnapshot();
  }

  private scheduleSnapshot(): void {
    const undoItemCount = this.undoList?.undoItems?.length || 0;
    const undoBulkSelectionCount = this.undoList?.undoBulkSelection?.length || 0;
    const currentAction = this.undoList?.currentAction;

    if (
      undoItemCount === this.undoItemCount &&
      undoBulkSelectionCount === this.undoBulkSelectionCount &&
      currentAction === this.currentAction
    ) {
      return;
    }

    // The list helper can clear undo state while a parent view is already being
    // checked. Keep one stable snapshot for the whole change-detection turn.
    if (!this.initialized) {
      this.applySnapshot();
      return;
    }

    if (this.syncScheduled) {
      return;
    }
    this.syncScheduled = true;
    deferSignalStateWrite(() => {
      this.syncScheduled = false;
      this.applySnapshot();
    });
  }

  private applySnapshot(): void {
    this.undoItemCount = this.undoList?.undoItems?.length || 0;
    this.undoBulkSelectionCount = this.undoList?.undoBulkSelection?.length || 0;
    this.currentAction = this.undoList?.currentAction;
    this.initialized = true;
  }
}
